import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import AuthShell from "../components/AuthShell";

// Maps the granular occupation_type to the platform's `role` enum.
// Owners/operators get the `owner` role; everyone else is a `student` (resident-like).
const OCCUPATIONS = [
  { value: "student", label: "Student", role: "student" },
  { value: "working_professional", label: "Working Professional", role: "student" },
  { value: "short_stay", label: "Short Stay Guest", role: "student" },
  { value: "freelancer", label: "Freelancer / Remote Worker", role: "student" },
  { value: "owner", label: "PG Owner / Operator", role: "owner" },
  { value: "other", label: "Other", role: "student" },
];

const inputCls =
  "w-full mt-1.5 px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-white/30 focus:border-[#34C97B] focus:ring-2 focus:ring-[#34C97B]/30 outline-none transition";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    city: "",
    occupation_type: "student",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const occ = OCCUPATIONS.find((o) => o.value === form.occupation_type) || OCCUPATIONS[0];
    const payload = { ...form, role: occ.role };
    const r = await register(payload);
    setBusy(false);
    if (r.ok) {
      toast.success("Account created!");
      const home =
        { owner: "/dashboard", student: "/dashboard", admin: "/dashboard", vendor: "/vendors/dashboard" }[r.user?.role] ||
        "/dashboard";
      nav(home);
    } else setErr(r.error);
  };

  return (
    <AuthShell
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-[#34C97B] hover:underline" data-testid="link-login">
            Sign in
          </Link>
        </>
      }
    >
      <div data-testid="register-page">
        <h1 className="text-2xl font-bold text-white">Create your account</h1>
        <p className="text-sm text-white/60 mt-1">Takes less than 60 seconds.</p>

        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-white/60">I am a...</label>
            <select
              className={inputCls}
              value={form.occupation_type}
              onChange={(e) => set("occupation_type", e.target.value)}
              data-testid="register-occupation"
              required
            >
              {OCCUPATIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-[#0D1B2A] text-white">
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Full name</label>
              <input
                className={inputCls}
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Aman Sharma"
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Phone</label>
              <input
                className={inputCls}
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+91 98765 43210"
                data-testid="register-phone"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Email</label>
              <input
                type="email"
                className={inputCls}
                required
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@email.com"
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-white/60">Password (min 6)</label>
              <input
                type="password"
                className={inputCls}
                required
                minLength={6}
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder="••••••••"
                data-testid="register-password"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-white/60">City</label>
              <input
                className={inputCls}
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="Jaipur, Bengaluru, Pune..."
                data-testid="register-city"
                required
              />
            </div>
          </div>

          {err && (
            <div
              className="bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-xl p-3 text-sm font-medium"
              data-testid="register-error"
            >
              {err}
            </div>
          )}

          <button
            className="w-full inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition"
            disabled={busy}
            data-testid="register-submit"
          >
            {busy ? "Creating..." : (<>Create account <ArrowRight size={16} /></>)}
          </button>
        </form>
      </div>
    </AuthShell>
  );
}
